import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  HomeIcon,
  StarIcon,
  TrashIcon,
  LinkIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  FilmIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useUIStore } from '../../stores/uiStore';
import { useIsAdmin } from '../../stores/authStore';
import { filesApi } from '../../api/files';
import { formatShortDate } from '../../utils/dateFormat';
import type { FileItem } from '../../stores/fileStore';
import clsx from 'clsx';

const navigationKeys = [
  { key: 'allFiles', href: '/', icon: HomeIcon },
  { key: 'clients', href: '/clients', icon: UserGroupIcon },
  { key: 'diagnostics', href: '/diagnostics', icon: ClipboardDocumentListIcon },
  { key: 'starred', href: '/starred', icon: StarIcon },
  { key: 'sharedLinks', href: '/links', icon: LinkIcon },
  { key: 'trash', href: '/trash', icon: TrashIcon },
];

const adminNavigationKeys = [
  { key: 'adminUsers', href: '/admin/users', icon: UsersIcon },
];

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return PhotoIcon;
  if (mimeType.startsWith('video/')) return FilmIcon;
  if (mimeType.includes('pdf') || mimeType.includes('document')) return DocumentTextIcon;
  return DocumentIcon;
}

function getFileIconColor(mimeType: string) {
  if (mimeType.startsWith('image/')) return 'text-pink-500 bg-pink-50 dark:bg-pink-900/20';
  if (mimeType.startsWith('video/')) return 'text-purple-500 bg-purple-50 dark:bg-purple-900/20';
  if (mimeType.includes('pdf') || mimeType.includes('document'))
    return 'text-red-500 bg-red-50 dark:bg-red-900/20';
  return 'text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-800';
}

export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarCollapsed = useUIStore((s) => s.isSidebarCollapsed);
  const isAdmin = useIsAdmin();

  const [recentFiles, setRecentFiles] = useState<FileItem[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);
  const hasFetched = useRef(false);

  // Fetch recent files on mount and when navigating
  useEffect(() => {
    let cancelled = false;

    const fetchRecent = async () => {
      try {
        const response = await filesApi.list({
          sortBy: 'updatedAt',
          sortOrder: 'desc',
          limit: 5,
        });
        if (!cancelled) {
          setRecentFiles(response.files);
        }
      } catch {
        // Silently fail - quick access is non-critical
      } finally {
        if (!cancelled) setIsLoadingRecent(false);
      }
    };

    // Fetch immediately on first mount, then debounce on navigation
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchRecent();
    } else {
      const timer = setTimeout(fetchRecent, 500);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Handle recent file click
  const handleFileClick = (file: FileItem) => {
    if (file.name.endsWith('.md')) {
      navigate(`/documents/${file.id}`);
    } else if (file.folderId) {
      navigate(`/folder/${file.folderId}`);
    } else {
      navigate('/');
    }
  };

  return (
    <motion.aside
      layout
      className={clsx(
        'fixed left-0 top-16 bottom-0 bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-700 z-30',
        'transition-all duration-300 overflow-hidden',
        isSidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex flex-col h-full p-3">
        {/* Navigation */}
        <nav className="flex-1 space-y-1">
          {navigationKeys.map((item) => {
            const isActive =
              item.href === '/'
                ? location.pathname === '/' || location.pathname.startsWith('/folder')
                : item.href === '/clients'
                  ? location.pathname.startsWith('/clients')
                  : item.href === '/diagnostics'
                    ? location.pathname.startsWith('/diagnostics')
                    : location.pathname === item.href;

            return (
              <NavLink
                key={item.key}
                to={item.href}
                className={clsx(
                  'flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors',
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                    : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800',
                  isSidebarCollapsed && 'px-3 justify-center'
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!isSidebarCollapsed && <span className="font-medium">{t(`nav.${item.key}`)}</span>}
              </NavLink>
            );
          })}

          {/* Admin section - only visible for admins */}
          {isAdmin && (
            <>
              <div className="my-4 border-t border-gray-200 dark:border-slate-700" />
              {!isSidebarCollapsed && (
                <div className="px-4 mb-2 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                  {t('nav.admin')}
                </div>
              )}
              {adminNavigationKeys.map((item) => {
                const isActive = location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={clsx(
                      'flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors',
                      isActive
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                        : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800',
                      isSidebarCollapsed && 'px-3 justify-center'
                    )}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {!isSidebarCollapsed && <span className="font-medium">{t(`nav.${item.key}`)}</span>}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        {/* Quick Access - recent files */}
        {!isSidebarCollapsed && (
          <div className="pt-3 border-t border-gray-200 dark:border-slate-700">
            <div className="px-2 mb-2 flex items-center gap-2">
              <ClockIcon className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
              <span className="text-[11px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                {t('sidebar.quickAccess')}
              </span>
            </div>

            <div className="space-y-0.5">
              {isLoadingRecent ? (
                // Loading skeleton
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-2 py-1.5">
                    <div className="h-6 w-6 rounded-md bg-gray-100 dark:bg-slate-800 animate-pulse" />
                    <div className="flex-1 h-3.5 rounded bg-gray-100 dark:bg-slate-800 animate-pulse" />
                  </div>
                ))
              ) : recentFiles.length > 0 ? (
                recentFiles.map((file, index) => {
                  const Icon = getFileIcon(file.mimeType);
                  const iconColorClass = getFileIconColor(file.mimeType);

                  return (
                    <motion.button
                      key={file.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.2 }}
                      onClick={() => handleFileClick(file)}
                      className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/70 transition-colors group"
                    >
                      <div
                        className={clsx(
                          'h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0',
                          iconColorClass
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <span className="flex-1 text-[13px] text-gray-600 dark:text-slate-400 truncate text-left group-hover:text-gray-900 dark:group-hover:text-slate-200 transition-colors">
                        {file.name}
                      </span>
                      <span className="text-[10px] text-gray-300 dark:text-slate-600 flex-shrink-0 tabular-nums">
                        {formatShortDate(file.updatedAt)}
                      </span>
                    </motion.button>
                  );
                })
              ) : (
                <p className="px-2 py-2 text-xs text-gray-400 dark:text-slate-600">
                  {t('sidebar.noRecentFiles')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Collapsed: clock icon for quick access */}
        {isSidebarCollapsed && recentFiles.length > 0 && (
          <div className="py-2 border-t border-gray-200 dark:border-slate-700 flex justify-center">
            <div
              className="p-2 rounded-lg text-gray-400 dark:text-slate-500"
              title={t('sidebar.quickAccess')}
            >
              <ClockIcon className="h-5 w-5" />
            </div>
          </div>
        )}

      </div>
    </motion.aside>
  );
}
